import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import styles from './SelectMenu.module.css';

export type OpcaoSelect = {
  id: string;
  rotulo: string;
  icone?: string;
  cor?: string;
};

export type SelectMenuProps = {
  label?: string;
  valor?: string;
  onChange?: (id: string) => void;
  opcoes: OpcaoSelect[];
  placeholder?: string;
  busca?: boolean;
  erro?: string;
  /**
   * Modo MÚLTIPLO: passe `valores` + `onMudarValores` (e ignore valor/onChange).
   * Cada clique alterna a opção e devolve a lista completa; o painel fica
   * aberto pra selecionar vários de uma vez.
   */
  valores?: string[];
  onMudarValores?: (ids: string[]) => void;
};

const normalizar = (texto: string) =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export default function SelectMenu({
  label,
  valor,
  onChange,
  opcoes,
  placeholder,
  busca,
  erro,
  valores,
  onMudarValores
}: SelectMenuProps) {
  const [aberto, setAberto] = useState(false);
  const [filtro, setFiltro] = useState('');
  const raizRef = useRef<HTMLDivElement>(null);

  const multiplo = !!onMudarValores;
  const marcadas = valores ?? [];
  const selecionada = multiplo
    ? opcoes.find((opcao) => opcao.id === marcadas[0])
    : opcoes.find((opcao) => opcao.id === valor);
  const filtradas = filtro
    ? opcoes.filter((opcao) => normalizar(opcao.rotulo).includes(normalizar(filtro)))
    : opcoes;

  // Fecha em clique fora e Esc enquanto aberto.
  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (evento: MouseEvent) => {
      if (raizRef.current && !raizRef.current.contains(evento.target as Node)) {
        setAberto(false);
        setFiltro('');
      }
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        setAberto(false);
        setFiltro('');
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const selecionar = (id: string) => {
    if (multiplo) {
      // Alterna e MANTÉM o painel aberto: o caso de uso é marcar vários.
      onMudarValores?.(
        marcadas.includes(id) ? marcadas.filter((m) => m !== id) : [...marcadas, id]
      );
      return;
    }
    onChange?.(id);
    setAberto(false);
    setFiltro('');
  };

  return (
    <div className={styles.raiz} ref={raizRef}>
      {label && <span className={styles.rotulo}>{label}</span>}
      <div className={styles.controle}>
        <button
          type="button"
          className={`${styles.campo} ${aberto ? styles.campoAberto : ''} ${erro ? styles.comErro : ''}`}
          onClick={() => setAberto((atual) => !atual)}
        >
          {selecionada?.icone && (
            <span
              className={styles.iconeOpcao}
              style={selecionada.cor ? { color: selecionada.cor } : undefined}
            >
              <Icon nome={selecionada.icone} tamanho={18} />
            </span>
          )}
          {selecionada ? (
            <span className={styles.valorTexto}>
              {selecionada.rotulo}
              {multiplo && marcadas.length > 1 ? ` +${marcadas.length - 1}` : ''}
            </span>
          ) : (
            <span className={styles.placeholderTexto}>{placeholder ?? 'Selecione'}</span>
          )}
          <span className={`${styles.seta} ${aberto ? styles.setaAberta : ''}`}>
            <Icon nome="expand_more" tamanho={18} />
          </span>
        </button>

        {aberto && (
          <div className={styles.painel}>
            {busca && (
              <div className={styles.busca}>
                <Icon nome="search" tamanho={16} />
                <input
                  className={styles.buscaInput}
                  type="text"
                  value={filtro}
                  onChange={(evento) => setFiltro(evento.target.value)}
                  placeholder="Buscar..."
                  autoFocus
                />
              </div>
            )}
            {filtradas.map((opcao) => {
              const ativa = multiplo ? marcadas.includes(opcao.id) : opcao.id === valor;
              return (
                <button
                  key={opcao.id}
                  type="button"
                  className={`${styles.opcao} ${ativa ? styles.selecionada : ''}`}
                  onClick={() => selecionar(opcao.id)}
                >
                  {opcao.icone && (
                    <span
                      className={styles.iconeOpcao}
                      style={opcao.cor ? { color: opcao.cor } : undefined}
                    >
                      <Icon nome={opcao.icone} tamanho={18} />
                    </span>
                  )}
                  <span className={styles.rotuloOpcao}>{opcao.rotulo}</span>
                  {ativa && (
                    <span className={styles.check}>
                      <Icon nome="check" tamanho={16} />
                    </span>
                  )}
                </button>
              );
            })}
            {filtradas.length === 0 && <div className={styles.vazio}>Nenhuma opção encontrada</div>}
          </div>
        )}
      </div>
      {erro && <span className={styles.erro}>{erro}</span>}
    </div>
  );
}
